'use client';

import * as React from 'react';
import type { CultivFlowCopy } from '../types';

/* Wrapper around the framework-agnostic canvas component in /public/cultiv/flow.js.
   flow.js attaches window.CultivFlow.mount(el, { copy, scrollDrive, height }) → { destroy }.
   It touches window/canvas, so it must run client-side only; we inject the script once
   and mount when it is ready. */

type FlowInstance = { destroy: () => void };
type FlowGlobal = {
  mount: (
    el: HTMLElement,
    opts: { copy: CultivFlowCopy; scrollDrive: boolean; height: number; title?: string },
  ) => FlowInstance;
};

declare global {
  interface Window {
    CultivFlow?: FlowGlobal;
  }
}

const FLOW_SRC = '/cultiv/flow.js';
const FLOW_SCRIPT_ID = 'cultiv-flow-js';

export function FlowSim({
  flow,
  scrollDrive,
  height = 800,
  title,
}: {
  flow: CultivFlowCopy;
  scrollDrive?: boolean;
  height?: number;
  title?: string;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    let inst: FlowInstance | null = null;
    let cancelled = false;

    const mount = () => {
      if (cancelled || !ref.current || !window.CultivFlow) return;
      inst = window.CultivFlow.mount(ref.current, {
        copy: flow,
        scrollDrive: !!scrollDrive,
        height,
        title,
      });
    };
    // if the script 404s, hide the host instead of leaving an invisible blank band
    const onError = () => {
      console.error(`FlowSim: failed to load ${FLOW_SRC}`);
      if (ref.current) ref.current.style.display = 'none';
    };

    let script: HTMLScriptElement | null = null;
    if (window.CultivFlow) {
      mount();
    } else {
      script = document.getElementById(FLOW_SCRIPT_ID) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = FLOW_SCRIPT_ID;
        script.src = FLOW_SRC;
        script.async = true;
        document.body.appendChild(script);
      }
      script.addEventListener('load', mount, { once: true });
      script.addEventListener('error', onError, { once: true });
    }

    return () => {
      cancelled = true;
      if (script) {
        script.removeEventListener('load', mount);
        script.removeEventListener('error', onError);
      }
      if (inst) inst.destroy();
    };
  }, [flow, scrollDrive, height, title]);

  return <div className="b-flowsim-host" ref={ref} />;
}
